import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "../services/api";
import { 
  ChevronLeft, 
  Clock, 
  ShieldCheck, 
  ArrowRight,
  Loader2,
  CalendarDays
} from "lucide-react";

interface AgeOption {
  id: number;
  title: string;
  weight: number;
  image: string | null;
}

const DeviceAge: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceId, variantId } = useParams();

  const { groupId } = location.state || {};

  const [options, setOptions] = useState<AgeOption[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // --- CORE LOGIC (UNTOUCHED) ---
  useEffect(() => {
    if (!groupId) {
      setLoading(false);
      return;
    }

    api.get(`/inspection/group/${groupId}/deviceage`)
      .then((data) => setOptions(data))
      .catch(() => alert("Failed to load age options"))
      .finally(() => setLoading(false));
  }, [groupId]);

  const handleContinue = () => {
    if (selectedId === null) return;

    navigate(`/sell/accessories/${deviceId}/${variantId}`, {
      state: {
        ...location.state,
        deviceAge: [selectedId],
      },
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-10 h-10 text-[#363C90] animate-spin" />
        <p className="text-slate-400 text-sm font-bold uppercase tracking-widest italic">Calculating Life Cycle...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-32">
      
      {/* --- SLIM STICKY HEADER --- */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-[800px] mx-auto px-4 py-4 md:py-6 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-50 rounded-xl transition-colors text-slate-400"
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight leading-none">Device Age</h1>
            <p className="text-slate-400 text-[12px] font-medium mt-1 italic tracking-tight">How long have you owned this device?</p>
          </div>
        </div>
      </div>

      <div className="max-w-[700px] mx-auto px-4 mt-10">
        
        {/* --- OPTIONS LIST (SINGLE SELECT) --- */}
        <div className="space-y-3">
          {options.map((item) => {
            const isSelected = selectedId === item.id;
            return (
              <div
                key={item.id}
                onClick={() => setSelectedId(item.id)}
                className={`
                  group flex items-center p-5 rounded-2xl cursor-pointer border transition-all duration-200
                  ${isSelected 
                    ? "border-[#363C90] bg-indigo-50/30 shadow-md ring-1 ring-[#363C90]" 
                    : "border-slate-100 bg-white hover:border-slate-200"}
                `}
              >
                {/* Custom Radio Button Pattern */}
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                  ${isSelected ? "border-[#363C90] bg-[#363C90]" : "border-slate-200 bg-white group-hover:border-slate-300"}
                `}>
                  {isSelected && <div className="w-2 h-2 rounded-full bg-white shadow-sm" />}
                </div>

                <div className="flex-1 ml-5">
                  <h3 className={`text-md font-bold tracking-tight ${isSelected ? "text-[#363C90]" : "text-slate-700"}`}>
                    {item.title}
                  </h3>
                </div>

                <div className={`p-2 rounded-lg transition-colors ${isSelected ? "bg-indigo-100/50 text-[#363C90]" : "bg-slate-50 text-slate-300"}`}>
                  <CalendarDays size={18} />
                </div>
              </div>
            );
          })}
        </div>

        {/* --- SHARP INFO TIP --- */}
        <div className="mt-10 p-5 bg-emerald-50/50 border border-emerald-100 rounded-2xl flex items-start gap-4">
          <div className="p-2.5 bg-white rounded-xl shadow-sm border border-emerald-50">
            <ShieldCheck size={18} className="text-emerald-500" />
          </div>
          <div>
            <p className="text-[10px] font-black text-emerald-600 uppercase tracking-wider mb-1">Expert Tip</p>
            <p className="text-[13px] text-emerald-800/80 leading-snug font-medium italic">
              Accurate age selection helps us verify your remaining warranty and ensures a smoother, faster payout process.
            </p>
          </div>
        </div>
      </div>

      {/* --- SHARP FLOATING FOOTER --- */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-slate-100 p-4 z-40">
        <div className="max-w-[700px] mx-auto">
          <button
            disabled={selectedId === null}
            onClick={handleContinue}
            className={`
              w-full py-4 rounded-xl font-black text-[13px] flex items-center justify-center gap-2 transition-all uppercase tracking-[2px]
              ${selectedId !== null 
                ? "bg-[#363C90] text-white hover:bg-[#2a2f70] shadow-xl shadow-indigo-900/20 hover:scale-[1.01]" 
                : "bg-slate-100 text-slate-300 cursor-not-allowed border border-slate-200"}
            `}
          >
            Select Accessories <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeviceAge;